import React, { useEffect, useMemo } from 'react';
import { IconPlus } from '@tabler/icons-react';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { useWatch } from 'antd/es/form/Form';
import { journalTexts } from 'consts/text';
import { formFields } from 'consts/form/formFields';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { CommonFormProps, GroupTopicFormFields } from 'types';
import { AppBaseSelect, AppFormItem } from 'components/atoms';
import { ModalTreePickerElement } from 'components/molecules';
import {
    EducationalGuideTextbookId,
    EducationalGuideTopicId,
} from 'types/educationalGuide/educationalGuide';
import { AppCustomSelect } from 'components/atoms/CommonAppComponents/AppSelect/AppCustomSelect/AppCustomSelect';
import {
    mapTextbooksListToSelectOptionsList,
    mapUnitTopicsListToFlatList,
    parseTextbookToTreeSelect,
} from './utils';
import { useToggle } from '../../../../hooks/useToggle/useToggle';

type TopicEducationalGuideFormSectionProps = Pick<CommonFormProps, 'mode'> & {
    lessonDate?: string;
};

export const TopicEducationalGuideFormSection = ({
    mode,
    lessonDate,
}: TopicEducationalGuideFormSectionProps) => {
    const { setFieldValue, setFields } = useFormInstance<GroupTopicFormFields>();

    const [initiallyLoaded, setInitiallyLoadedTrue] = useToggle(false);

    const educationalGuideSelectValue = useWatch<EducationalGuideTextbookId | undefined>(
        formFields.educationalGuide,
    );
    const educationalGuideTopicSelectValue = useWatch<EducationalGuideTopicId | undefined>(
        formFields.educationalGuideTopic,
    );

    const [isModalOpen, openModal, closeModal] = useToggle(false);

    const { data: educationalGuideTextbooks, isFetching: isEducationalGuideTextbooksDataFetching } =
        useAppQuery(
            'EDUCATIONAL_GUIDE_TEXTBOOKS',
            [lessonDate],
            () =>
                StaffHTTPService.educationalGuide.getEducationalGuideTextbooks(
                    lessonDate as string,
                ),
            { enabled: !!lessonDate, staleTime: Infinity },
        );

    const mappedEducationalGuideTextbooks = useMemo(
        () =>
            educationalGuideTextbooks
                ? mapTextbooksListToSelectOptionsList(educationalGuideTextbooks)
                : [],
        [educationalGuideTextbooks],
    );

    const {
        data: educationalGuideTextbookUnits,
        isFetching: isEducationalGuidesUnitsDataFetching,
    } = useAppQuery(
        'EDUCATIONAL_GUIDE_TEXTBOOK_UNITS',
        [educationalGuideSelectValue],
        () =>
            StaffHTTPService.educationalGuide.getEducationalGuideTextbookUnits(
                educationalGuideSelectValue as EducationalGuideTextbookId,
            ),
        { enabled: !!educationalGuideSelectValue, staleTime: Infinity },
    );

    const flattenEducationalGuideTopics = useMemo(
        () =>
            educationalGuideTextbookUnits
                ? mapUnitTopicsListToFlatList(educationalGuideTextbookUnits)
                : [],
        [educationalGuideTextbookUnits],
    );

    const { data: educationalGuideTopicData, isFetching: isEducationalGuideTopicDataFetching } =
        useAppQuery(
            'EDUCATIONAL_GUIDE_TOPIC',
            [educationalGuideTopicSelectValue],
            () =>
                StaffHTTPService.educationalGuide.getEducationalGuideTopic(
                    educationalGuideTopicSelectValue as EducationalGuideTopicId,
                ),
            { enabled: !!educationalGuideTopicSelectValue, staleTime: Infinity },
        );

    const onSaveHandler = (value: React.Key[]) => {
        setFieldValue(formFields.educationalGuideTopic, value[0]);
    };

    const parsedEducationalGuideUnitData = useMemo(
        () =>
            educationalGuideTextbookUnits
                ? parseTextbookToTreeSelect(educationalGuideTextbookUnits)
                : [],
        [educationalGuideTextbookUnits],
    );

    const handleClearEducationalGuideTopic = () => {
        setFieldValue(formFields.educationalGuideTopic, undefined);
    };

    useEffect(() => {
        if (educationalGuideTopicData) {
            closeModal();

            // Prevent form overwriting coreCurriculums from an existing lesson
            if (initiallyLoaded || mode === 'add') {
                setFields([
                    { name: formFields.name, value: educationalGuideTopicData?.name },
                    { name: formFields.description, value: educationalGuideTopicData?.description },
                    {
                        name: formFields.coreCurriculums,
                        value: educationalGuideTopicData.coreCurriculums,
                    },
                ]);
            } else {
                setFields([
                    { name: formFields.name, value: educationalGuideTopicData?.name },
                    { name: formFields.description, value: educationalGuideTopicData?.description },
                ]);
            }

            setInitiallyLoadedTrue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closeModal, educationalGuideTopicData, setFields, setInitiallyLoadedTrue]);

    return (
        <>
            <AppFormItem
                label={journalTexts.journalDetails.groupJournal.topics.educationalGuideTextbook}
                name={formFields.educationalGuide}
                optional
                hasOptionalText
                extra={
                    educationalGuideTextbooks?.length === 0
                        ? journalTexts.journalDetails.groupJournal.topics
                              .educationalGuideTextbooksEmptyState
                        : undefined
                }
            >
                <AppBaseSelect
                    onChange={(value) => {
                        setFields([
                            { name: formFields.educationalGuide, value },
                            { name: formFields.educationalGuideTopic, value: undefined },
                        ]);
                    }}
                    options={mappedEducationalGuideTextbooks}
                    loading={isEducationalGuideTextbooksDataFetching}
                    disabled={!lessonDate}
                    placeholder="Wybierz przewodnik edukacyjny"
                />
            </AppFormItem>
            <ModalTreePickerElement
                title={journalTexts.journalDetails.groupJournal.topics.educationalGuideTopic}
                open={isModalOpen}
                onCancel={closeModal}
                onSave={onSaveHandler}
                treeData={parsedEducationalGuideUnitData}
                initialValue={
                    educationalGuideTopicSelectValue ? [educationalGuideTopicSelectValue] : []
                }
                isLoading={isEducationalGuideTopicDataFetching}
                width={800}
                showCheckAll={false}
                showSearchBar
                multiple={false}
            />
            <AppFormItem
                label={journalTexts.journalDetails.groupJournal.topics.educationalGuideTopic}
                name={formFields.educationalGuideTopic}
                disabled={!educationalGuideSelectValue}
                optional
                hasOptionalText
            >
                <AppCustomSelect
                    value={educationalGuideTopicSelectValue}
                    suffixIcon={<IconPlus size={20} />}
                    onClick={openModal}
                    onClear={handleClearEducationalGuideTopic}
                    loading={isEducationalGuidesUnitsDataFetching}
                    disabled={!educationalGuideSelectValue}
                    options={flattenEducationalGuideTopics}
                    placeholder="Wybierz temat"
                />
            </AppFormItem>
        </>
    );
};
